// File: src/components/Answer/Answer.tsx

import React, { useMemo } from "react";
import { IconButton } from "@fluentui/react";
import { Copy16Regular, Checkmark16Regular, Print16Regular } from "@fluentui/react-icons";

import styles from "./Answer.module.css";
import { ChatAppResponse, PriceSource } from "../../api";
import { parseAnswerToReact } from "./AnswerParser";
import { PublicIcon } from "../Icons/PublicIcon";
import { WebCitations, WebCitationItem } from "./WebCitations";

interface Props {
    answer: ChatAppResponse;
    isSelected?: boolean;
    isStreaming: boolean;
    onCitationClicked: (filePath: string) => void;
    onThoughtProcessClicked: () => void;
    onSupportingContentClicked: () => void;
    onFollowupQuestionClicked?: (question: string) => void;
    showFollowupQuestions?: boolean;
    onCategorySubmit?: (formattedAnswer: string) => void;
    answerIndex?: number;
    branding?: {
        title?: string;
        logo?: string;
        primaryColor?: string;
    };
    onAnswersUpdate?: (formattedAnswer: string) => void;
    clearTriggerId?: number;
    hasActiveMetadata?: boolean;
    priceSource: PriceSource;
    siteId: "PUBLIC_GR" | "PUBLIC_CY";
    feedbackSlot?: React.ReactNode;
    /** Web research citations to render below the answer body (favicon chip cluster + popover). */
    webCitations?: WebCitationItem[];
    onCopyConversation?: () => void;
    isConversationCopied?: boolean;
    copyConversationLabel?: string;
    copiedConversationLabel?: string;
    stoppedMessageLabel?: string;
    onExportPdf?: () => void;
    exportPdfLabel?: string;
}

// Constants for special message types
const PROCESSING_MESSAGE = "Processing question";
const STOPPED_MESSAGE = "Response stopped by user.";

export const Answer = ({
    answer,
    isSelected,
    isStreaming,
    onCitationClicked,
    onThoughtProcessClicked,
    onSupportingContentClicked,
    onFollowupQuestionClicked,
    showFollowupQuestions,
    onCategorySubmit,
    answerIndex = 0,
    branding,
    onAnswersUpdate,
    clearTriggerId,
    hasActiveMetadata = false,
    priceSource,
    siteId,
    feedbackSlot,
    webCitations,
    onCopyConversation,
    isConversationCopied = false,
    copyConversationLabel = "Copy conversation so far",
    copiedConversationLabel = "Copied",
    stoppedMessageLabel = STOPPED_MESSAGE,
    onExportPdf,
    exportPdfLabel = "Print / export PDF"
}: Props) => {
    const followupQuestions = answer.choices[0].context.followup_questions;
    const messageContent = answer.choices[0].message.content;
    const hasThoughts = answer.choices[0].context.thoughts?.length > 0;
    const hasDataPoints = answer.choices[0].context.data_points?.length > 0;
    const canCopyConversation = !!onCopyConversation && !!messageContent && !isStreaming && messageContent !== PROCESSING_MESSAGE && messageContent !== STOPPED_MESSAGE;
    const canExportPdf = !!onExportPdf && !!messageContent && !isStreaming && messageContent !== PROCESSING_MESSAGE && messageContent !== STOPPED_MESSAGE;

    // If the message is our placeholder or stopped message, bypass parsing to avoid JSON errors.
    const parsedAnswer = useMemo(() => {
        if (messageContent === PROCESSING_MESSAGE || messageContent === STOPPED_MESSAGE) {
            return { answerContent: [], citations: [] };
        }
        return parseAnswerToReact(
            messageContent,
            isStreaming,
            onCitationClicked,
            onCategorySubmit,
            isSelected || false,
            onAnswersUpdate,
            clearTriggerId,
            priceSource,
            siteId
        );
    }, [answer, isStreaming, messageContent, onCitationClicked, onCategorySubmit, isSelected, onAnswersUpdate, clearTriggerId, priceSource, siteId]);

    return (
        <div className={`${styles.answerContainer} ${isSelected ? styles.selected : ""}`}>
            {/* Avatar section */}
            <div className={`${styles.answerIcon} ${hasActiveMetadata ? styles.pulsing : ""}`}>
                {branding?.logo ? (
                    <img
                        src={branding.logo}
                        alt="Logo"
                        style={{
                            width: "24px",
                            height: "24px",
                            borderRadius: "4px",
                            objectFit: "contain"
                        }}
                    />
                ) : (
                    <PublicIcon size={24} />
                )}
            </div>

            {/* Answer content */}
            <div className={styles.answerContent}>
                {/* Action buttons - only show if there's content */}
                <div className={styles.actionButtons} data-print="hide">
                    {hasThoughts && (
                        <button className={styles.actionButton} onClick={onThoughtProcessClicked} title="Show thought process">
                            <span style={{ fontSize: "16px" }}>✨</span>
                            Thought process
                        </button>
                    )}
                    {hasDataPoints && (
                        <button className={styles.actionButton} onClick={onSupportingContentClicked} title="Show supporting content">
                            <span style={{ fontSize: "16px" }}>📄</span>
                            Sources
                        </button>
                    )}
                </div>

                {/* Main answer text */}
                <div className={styles.answerText} data-answer-content="true">
                    {messageContent === PROCESSING_MESSAGE ? (
                        <div className={styles.processingMessage}>
                            <span className={styles.dot}></span>
                            <span className={styles.dot}></span>
                            <span className={styles.dot}></span>
                        </div>
                    ) : messageContent === STOPPED_MESSAGE ? (
                        <div className={styles.stoppedMessage}>{stoppedMessageLabel}</div>
                    ) : (
                        parsedAnswer.answerContent.map((fragment, index) => <React.Fragment key={index}>{fragment}</React.Fragment>)
                    )}
                </div>

                {(canCopyConversation || canExportPdf || !!webCitations?.length) && (
                    <div className={styles.answerUtilityRow} data-print="hide">
                        {canCopyConversation && (
                            <div className={styles.utilityCell}>
                                <IconButton
                                    className={`${styles.iconActionButton} ${isConversationCopied ? styles.iconActionButtonSuccess : ""}`}
                                    onClick={onCopyConversation}
                                    title={isConversationCopied ? copiedConversationLabel : copyConversationLabel}
                                    ariaLabel={isConversationCopied ? copiedConversationLabel : copyConversationLabel}
                                >
                                    {isConversationCopied ? <Checkmark16Regular /> : <Copy16Regular />}
                                </IconButton>
                            </div>
                        )}
                        {canExportPdf && (
                            <div className={styles.utilityCell}>
                                <IconButton
                                    className={styles.iconActionButton}
                                    onClick={onExportPdf}
                                    title={exportPdfLabel}
                                    ariaLabel={exportPdfLabel}
                                >
                                    <Print16Regular />
                                </IconButton>
                            </div>
                        )}
                        {!!webCitations?.length && (
                            <div className={`${styles.utilityCell} ${styles.sourcesUtilityCell}`}>
                                <WebCitations items={webCitations} className={styles.inlineWebCitations} />
                            </div>
                        )}
                    </div>
                )}

                {/* Citations section */}
                {!!parsedAnswer.citations.length && (
                    <div className={styles.citationSection} data-answer-content="true">
                        <span className={styles.citationLearnMore}>Sources:</span>
                        {parsedAnswer.citations.map((citation, i) => (
                            <a key={i} className={styles.citationLink} title={citation} onClick={() => onCitationClicked(citation)}>
                                [{i + 1}] {citation}
                            </a>
                        ))}
                    </div>
                )}

                {/* Follow-up questions */}
                {!!followupQuestions?.length && showFollowupQuestions && onFollowupQuestionClicked && (
                    <div className={styles.followupQuestionsList} data-print="hide">
                        <span className={styles.followupQuestionLearnMore}>Suggested follow-up questions:</span>
                        {followupQuestions.map((question, i) => (
                            <a key={i} className={styles.followupQuestion} onClick={() => onFollowupQuestionClicked(question)}>
                                • {question}
                            </a>
                        ))}
                    </div>
                )}

                {feedbackSlot && <div className={styles.feedbackButtonsContainer} data-print="hide">{feedbackSlot}</div>}

            </div>
        </div>
    );
};

export default Answer;
